import { Button, Container, Element, Label } from '@playcanvas/pcui';

import { Events } from '../events';
import { localize } from './localization';
import sceneIcon from './svg/open.svg'; 
import { AuthService, RemoteScene } from '../auth';

const formatDate = (iso: string) => {
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    } catch {
        return iso;
    }
};

const createSvg = (svgString: string, args = {}) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new Element({
        dom: new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement,
        ...args
    });
};

class RemoteScenesDialog extends Container {
    show: () => Promise<void>;
    hide: () => void;
    destroy: () => void;
    private rowMap: Map<string, { 
        progressFill: Container, 
        progressText: Label, 
        statusLabel: Label, 
        downloadBtn: Button 
    }> = new Map();

    constructor(events: Events, authService: AuthService, args = {}) {
        args = {
            ...args,
            id: 'remote-scenes-dialog',
            class: 'settings-dialog',
            hidden: true,
            tabIndex: -1
        };

        super(args);

        const dialog = new Container({
            id: 'dialog'
        });

        // header
        const headerIcon = createSvg(sceneIcon, { id: 'icon' });
        const headerText = new Label({ id: 'text', text: localize('user.scenes.header') });
        const header = new Container({ id: 'header' });
        header.append(headerIcon);
        header.append(headerText);

        // content
        const content = new Container({ id: 'content' });
        const scenesList = new Container({ id: 'remote-scenes-list' });
        content.append(scenesList);

        // footer
        const footer = new Container({ id: 'footer' });
        const closeButton = new Button({ class: 'button', text: localize('render.cancel') });
        const uploadButton = new Button({ class: 'button', text: localize('user.scenes.upload') });
        footer.append(closeButton);
        footer.append(uploadButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        // Dismissal: testers reported the window could not be closed by clicking away.
        // Close on Escape, and on a press that lands on the backdrop itself (the
        // full-screen overlay) rather than anywhere inside #dialog.
        const keydown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.hide();
            } else {
                e.stopPropagation();
            }
        };

        const backdropPointerDown = (e: PointerEvent) => {
            if (e.target === this.dom) {
                this.hide();
            }
        };

        // The list scrolls via #content, but the wheel was not scrolling it - only
        // dragging the scrollbar worked. Drive scrollTop directly so the wheel always
        // works regardless of what else the page does with the event.
        const wheel = (e: WheelEvent) => {
            const el = content.dom;
            if (el.scrollHeight <= el.clientHeight) {
                return;
            }

            // deltaMode: 0 = pixels, 1 = lines, 2 = pages
            const scale = e.deltaMode === 1 ? 16 : (e.deltaMode === 2 ? el.clientHeight : 1);

            el.scrollTop += e.deltaY * scale;
            e.preventDefault();
            e.stopPropagation();
        };

        content.dom.addEventListener('wheel', wheel, { passive: false });

        const updateProgress = (data: { id: string, progress: number, status: string, error?: string }) => {
            const row = this.rowMap.get(data.id);
            if (!row) return;

            const { progressFill, progressText, statusLabel, downloadBtn } = row;

            // Update Progress Bar
            progressFill.dom.style.width = `${data.progress}%`;
            progressText.text = `${Math.round(data.progress)}%`;
            
            // Update Status Badge
            statusLabel.text = data.status;
            statusLabel.class.remove('pending', 'processing', 'done', 'failed');
            statusLabel.class.add(data.status.toLowerCase().replace(/\s+/g, '-'));

            // Handle Completion/Failure
            if (data.status === 'Done') {
                downloadBtn.enabled = true;
                progressFill.dom.style.width = '100%';
            } else if (data.status === 'Failed') {
                downloadBtn.enabled = false;
                if (data.error) {
                    console.error(`Project ${data.id} failed: ${data.error}`);
                }
            } else {
                downloadBtn.enabled = false;
            }
        };

        events.on('remoteScene.progress', updateProgress);

        const updateList = (scenes: RemoteScene[]) => {
            scenesList.clear();
            this.rowMap.clear();

            if (scenes.length === 0) {
                scenesList.append(new Label({ text: 'No scenes found.', class: 'label' }));
                return;
            }

            scenes.forEach(scene => {
                const row = new Container({ class: 'remote-scene-row' });
                
                const info = new Container({ class: 'remote-scene-info' });
                const name = new Label({ text: scene.name, class: 'remote-scene-name' });
                
                // Progress UI
                const progressContainer = new Container({ class: 'remote-scene-progress-container' });
                const progressFill = new Container({ class: 'remote-scene-progress-fill' });
                const progressText = new Label({ text: `${Math.round(scene.progress)}%`, class: 'remote-scene-progress-text' });
                progressFill.dom.style.width = `${scene.progress}%`;
                progressContainer.append(progressFill);
                
                const meta = new Container({ class: 'remote-scene-meta' });
                const date = new Label({ text: formatDate(scene.timestamp), class: 'remote-scene-date' });
                const statusStr = scene.status ?? 'Pending';
                const status = new Label({
                    text: statusStr,
                    class: 'remote-scene-status'
                });
                status.class.add(statusStr.toLowerCase().replace(/\s+/g, '-'));
                meta.append(date);
                meta.append(status);

                info.append(name);
                info.append(progressContainer);
                info.append(progressText);
                info.append(meta);

                const actions = new Container({ class: 'remote-scene-actions' });
                const downloadBtn = new Button({
                    text: localize('user.scenes.download'),
                    class: 'button',
                    enabled: scene.status === 'Done'
                });
                
                downloadBtn.on('click', async () => {
                    const originalText = downloadBtn.text;
                    downloadBtn.enabled = false;
                    downloadBtn.text = 'Downloading...';

                    const abortController = new AbortController();

                    events.fire('progressStart', `Downloading "${scene.name}"`);
                    events.fire('progressUpdate', { text: 'Starting…', progress: 0 });
                    events.fire('progressShowCancel', () => abortController.abort());

                    const blob = await authService.downloadScene(scene, (loaded, total) => {
                        const loadedMB = (loaded / 1048576).toFixed(1);
                        if (total > 0) {
                            const pct = Math.min(99, Math.round((loaded / total) * 100));
                            const totalMB = (total / 1048576).toFixed(1);
                            events.fire('progressUpdate', { text: `${loadedMB} / ${totalMB} MB`, progress: pct });
                        } else {
                            events.fire('progressUpdate', { text: `${loadedMB} MB downloaded`, progress: 0 });
                        }
                    }, abortController.signal);

                    if (blob) {
                        events.fire('progressUpdate', { text: 'Opening scene…', progress: 100 });
                        events.fire('progressShowCancel', null);

                        // Save to browser's default download folder
                        const filename = scene.name.endsWith('.blgs') ? scene.name : `${scene.name}.blgs`;
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        setTimeout(() => URL.revokeObjectURL(url), 1000);

                        await events.invoke('import', [{ filename, contents: blob as any }]);
                    }

                    events.fire('progressEnd');
                    downloadBtn.text = originalText;
                    downloadBtn.enabled = true;
                    this.hide();
                });

                actions.append(downloadBtn);
                row.append(info);
                row.append(actions);
                scenesList.append(row);

                // Add to map for real-time updates
                this.rowMap.set(scene.id, {
                    progressFill,
                    progressText,
                    statusLabel: status,
                    downloadBtn
                });
            });
        };

        uploadButton.on('click', async () => {
            this.hide();
            const uploaded = await events.invoke('user.uploadScene');
            if (uploaded) {
                const updatedScenes = await authService.getRemoteScenes();
                updateList(updatedScenes);
            }
            this.show();
        });

        closeButton.on('click', () => this.hide());

        this.show = async () => {
            scenesList.clear();
            scenesList.append(new Label({ text: 'Loading...', class: 'label' }));
            this.hidden = false;
            this.dom.addEventListener('keydown', keydown);
            this.dom.addEventListener('pointerdown', backdropPointerDown);
            // The dialog is tabIndex -1 and has no input to autofocus, so focus it
            // explicitly - keydown does not fire on an unfocused subtree.
            setTimeout(() => this.dom.focus(), 0);

            const scenes = await authService.getRemoteScenes();
            updateList(scenes);
        };

        this.hide = () => {
            this.dom.removeEventListener('keydown', keydown);
            this.dom.removeEventListener('pointerdown', backdropPointerDown);
            this.hidden = true;
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { RemoteScenesDialog };
